/**
 * A destructive action behind a confirmation.
 *
 * WHY IT EXISTS: `deleteNeverPublishedDraft` IS A HARD DELETE — "The draft is gone", 204, no body,
 * no restore route anywhere on the surface. The prototype draws no confirmation for it at all
 * (there is no `>Delete` control in the prototype), so this is ours to get right rather than ours
 * to copy.
 *
 * THE CONFIRMATION NAMES THE THING. "Delete this draft?" with the title in it, not "Are you sure?"
 * — a dialog that does not say what it is about is a dialog people click through.
 */
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';

export interface ConfirmActionProps {
  /** The control that opens the dialog. Given an onClick by this component. */
  trigger: (open: () => void) => ReactNode;
  title: string;
  /** What will happen, and whether it can be undone. */
  body: ReactNode;
  confirmLabel: string;
  /** True for anything with no way back. Colours the confirm control with the danger token. */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmAction({
  trigger,
  title,
  body,
  confirmLabel,
  destructive = false,
  busy = false,
  onConfirm,
}: ConfirmActionProps) {
  const { t } = useTranslation('cabinet');
  const [opened, setOpened] = useState(false);

  return (
    <>
      {trigger(() => {
        setOpened(true);
      })}
      <Modal
        opened={opened}
        onClose={() => {
          setOpened(false);
        }}
        title={title}
        size="md"
      >
        <Stack gap="lg">
          <Text size="sm">{body}</Text>
          <Group justify="flex-end">
            {/* ★ THE DIALOG OPENS ON CANCEL, never on the destructive choice
                (docs/ui/03-components.md:143). Mantine's focus trap takes the first tabbable
                element - the × - unless an element carries `data-autofocus`, which it honours
                first, as the back office's `ConfirmDialog` does. */}
            <Button
              variant="default"
              data-autofocus
              onClick={() => {
                setOpened(false);
              }}
            >
              {t('confirm.cancel')}
            </Button>
            <Button
              loading={busy}
              variant="filled"
              {...(destructive
                ? {
                    styles: {
                      root: {
                        backgroundColor: 'oklch(var(--mx-danger))',
                        color: 'oklch(var(--mx-on-danger))',
                      },
                    },
                  }
                : {})}
              onClick={() => {
                void (async () => {
                  await onConfirm();
                  setOpened(false);
                })();
              }}
            >
              {confirmLabel}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
